import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  GetContextMenuItemsParams, GetLocaleTextParams,
  GridReadyEvent,
  IServerSideGetRowsParams, ProcessCellForExportParams,
  RowClassRules, RowNode, SendToClipboardParams
} from 'ag-grid-community';
import {Observable, share, take} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {SharedService} from '../services/shared.service';
import {BulkEditDialogComponent} from './dialogs/bulk-edit-dialog/bulk-edit-dialog.component';
import {ToastService} from '../services/toast.service';
import {environment} from '../../environments/environment';
import {WebSocketService} from '../services/web-socket.service';
import {GridService} from '../services/grid.service';
import {HttpService} from '../services/http.service';
import {AuthService} from '../services/auth.service';
import {shareReplay} from 'rxjs/operators';
import {Router} from '@angular/router';
import {HistoryDialogComponent} from './dialogs/history-dialog/history-dialog.component';
import { LocalStorageService } from '../services/local-storage.service';
import {InfoService} from '../services/info.service';
import {FeedToolPanelComponent} from "./components/feed-tool-panel/feed-tool-panel.component";

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss']
})
export class GridComponent implements OnInit, OnDestroy {
  public user$: Observable<any>;
  public readonly rowModelType = 'serverSide';
  public readonly paginationPageSize = 1000;
  public readonly cacheBlockSize = 37; // 300
  public readonly maxBlocksInCache = this.cacheBlockSize / 3;
  public readonly maxConcurrentDatasourceRequests = 1;
  public readonly rowGroupPanelShow = 'never';
  public readonly pivotPanelShow = 'always';
  public readonly rowSelection = 'multiple';
  public readonly undoRedoCellEditing = false;
  public readonly undoRedoCellEditingLimit = 4;
  public readonly enableCellChangeFlash = true;
  public readonly overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait...</span>';
  public pinnedTopRowData: any[] = [];
  public readonly sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        }
      },
      // https://www.ag-grid.com/angular-data-grid/component-tool-panel/
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
        toolPanelParams: {
          suppressExpandAll: false,
          suppressFilterSearch: false
        }
      },
      // {
      //   id: 'feedsPanel',
      //   labelDefault: 'Feeds',
      //   labelKey: 'customStats',
      //   iconKey: 'pivotPanel',
      //   toolPanel: FeedToolPanelComponent,
      // },
    ],
  };
  private gridReady: boolean = false;

  public readonly autoGroupColumnDef: ColDef = {
    minWidth: 200,
  }
  public rowClassRules: RowClassRules = {
    'not-valid': (params) => {
      if (params.data && (!params.data.handle || !params.data.brand)) {
        return true
      } else {
        return false;
      }
    },
    'sync-in-progress': (params) => {
      if (params.data) {
        return params.data.shopify_sync_status === 2;
      } else {
        return false;
      }
    },
    'product-change-icon' : (params) => {
      return !!(params.data && [20, 30, 40].includes(params.data.status));
    },
    'editing-row': (params) => {
      if (params.data && params.data.locked && params.data.locked.user && (params.data.locked.user._id !== this.auth.currentUserValue._id)) {
        return true;
      } else {
        return false;
      }
    },
  };
  public readonly statusBar = {
    statusPanels: [
      { statusPanel: 'agSelectedRowCountComponent' }
    ],
  };

  public getLocaleText: (params: GetLocaleTextParams) => string = (
    params: GetLocaleTextParams
  ) => {
    console.log(params);
    switch (params.key) {
      case 'containsWithAbc':
        return 'Contains With Abc';
      case 'decimalSeparator':
        return ',';
      default:
        return params.defaultValue;
    }
  };

  public filterIsActive = false;
  public selectedItemsCount = 0;
  public allChangedItemsCount = 0;
  public allChangedItemsLogsCount = 0;
  public pendingCancel = false;

  public columnDefs: ColDef[] = this.sharedService.columnDefs;
  public defaultColDef: ColDef = {
    flex: 1,
    minWidth: 170,
    sortable: true,
    editable: true,
    filter: true,
    // floatingFilter: true,
    resizable: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset'],
    },
    cellRenderer: (cell: any) => {
      if (!['tags', 'vendor_tags'].includes(cell.colDef.field)) {
        return cell.value;
      }
    },

    cellClass: (params: any) => {
      const cellChanged = params.data?.fields_status?.[params.colDef.field] === 20;
      const cellLock = params.data.locked !== undefined && Object.keys(params.data.locked).length > 0;
      const cellLockOtherUsers = cellLock ? params.data.locked && params.data.locked.user && params.data.locked.user._id !== this.auth.currentUserValue?._id: false;
      const cellPrice = (params.colDef.field === 'list_price' || params.colDef.field === 'cost_price'  || params.colDef.field === 'sell_price' || params.colDef.field === 'mws_sell_price');

      if (cellPrice && cellChanged) {
        return ['number-cell', 'ag-cell-changed'];
      } else if (cellChanged) {
        if (cellLockOtherUsers) {
          return '';
        }
        return 'ag-cell-changed';
      } else if (params.colDef.field === 'sku') {
        return 'sku-col';
      } else if (cellPrice) {
        return 'number-cell';
      }

      return '';
    }
  };
  public rowData$!: Observable<any[]>;

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private dialog: MatDialog,
    private sharedService: SharedService,
    private toastService: ToastService,
    private gridService: GridService,
    private auth: AuthService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private webSocketService: WebSocketService,
    private infoService: InfoService,
  ) {
    this.user$ = this.auth.getCurrentUser().pipe(
      shareReplay()
    );
  }

  sendToClipboard(params: SendToClipboardParams) {
    console.log('send to clipboard called with data:');
    console.log(params);
  }

  processCellForClipboard(params: ProcessCellForExportParams) {
    const column = params.column.getId();
    if (['brand', 'type', 'shipping_tag'].includes(column)) {
      return params.value.name;
    } else if (column === 'datasheet_new') {
      return params.value.map((x: any) => x.url).join(', ');
    } else if (['tags', 'vendor_tags'].includes(column)) {
      return params.value.map((tag: any) => tag.name).join(', ');
    } else if (column === 'related_products' || column === 'related_parts') {
      return params.value.map((t: any) => t.product_id).join(', ');
    } else {
      return params.value;
    }
  }

  ngOnInit(): void {
    this.infoService.getChangedStageProductsCounter();
    this.httpService.post(environment.API_PATH + '/product/stage/edit/stop', {}).subscribe((res: any) => {});
    const confirmationRow = this.columnDefs.find(x => x.field === 'confirm');
    const changeByUserRow = this.columnDefs.find(x => x.field === 'changed_by');
    const pushedAtRow = this.columnDefs.find(x => x.field === 'pushed_at');
    const pushedUserRow = this.columnDefs.find(x => x.field === 'pushed_user');
    const skuRow = this.columnDefs.find(x => x.field === 'sku');
    const updatedAtRow = this.columnDefs.find(x => x.field === 'updated_at');
    const statusRow = this.columnDefs.find(x => x.field === 'status');
    const pricing_idRow = this.columnDefs.find(x => x.field === 'pricing_id');
    const update_method = this.columnDefs.find(x => x.field === 'update_method');

    if (update_method) {
      update_method.hide = true;
    }

    if (statusRow) {
      statusRow.hide = false;
    }

    if (pricing_idRow) {
      pricing_idRow.editable = true;
    }

    if (updatedAtRow) {
      updatedAtRow.hide = true;
      updatedAtRow.sortable = false;
      updatedAtRow.sort = null;
      updatedAtRow.suppressFiltersToolPanel = true;
      updatedAtRow.suppressColumnsToolPanel = true;
    }

    if (changeByUserRow) {
      changeByUserRow.hide = true;
      changeByUserRow.sortable = false;
      changeByUserRow.suppressFiltersToolPanel = true;
      changeByUserRow.suppressColumnsToolPanel = true;
    }

    if (skuRow) {
      skuRow.pinned = 'left';
      skuRow.sort = 'asc';
      skuRow.checkboxSelection = true;
    }

    if (confirmationRow) {
      confirmationRow.hide = true;
      confirmationRow.pinned = false;
      confirmationRow.suppressFiltersToolPanel = true;
      confirmationRow.suppressColumnsToolPanel = true;
    }
    if (pushedAtRow) {
      pushedAtRow.hide = true;
      pushedAtRow.suppressFiltersToolPanel = true;
      pushedAtRow.suppressColumnsToolPanel = true;
      pushedAtRow.sort = null;
    }
    if (pushedUserRow) {
      pushedUserRow.hide = true;
      pushedUserRow.suppressFiltersToolPanel = true;
      pushedUserRow.suppressColumnsToolPanel = true;
    }
    this.gridService.changedItemsCount?.subscribe(res => {
      this.allChangedItemsCount = res;
    });

    this.gridService.changedItemsLogsCount?.subscribe(res => {
      this.allChangedItemsLogsCount = res;
    });
  }

  ngOnDestroy(): void {
    const pricing_idRow = this.columnDefs.find(x => x.field === 'pricing_id');
    if (pricing_idRow) {
      pricing_idRow.editable = false;
    }
    this.httpService.post(environment.API_PATH + '/product/stage/edit/stop', {}).subscribe((res: any) => {});
    this.gridService.stageSelectedItemsCount$.next(0);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridService.gridApi = params.api;
    this.gridService.columnApi = params.columnApi;
    const columnState = this.localStorageService.getItem('stageColumnState');
    // Strip saved sorts on array-valued columns — they are no longer
    // sortable, but a previously saved sort would still be applied by
    // applyColumnState and re-trigger the 10s server-side array sort.
    const unsortableCols = ['collections', 'related_products', 'related_parts', 'related_collections', 'tags', 'vendor_tags', 'asm_tags'];
    if (Array.isArray(columnState)) {
      for (const col of columnState) {
        if (unsortableCols.includes(col.colId)) { col.sort = null; }
      }
    }
    params.columnApi?.applyColumnState({
      state: columnState,
      applyOrder: true
    });
    const that = this;
      const prevFilterModel = this.localStorageService.getItem('stageFilterModel');
      if (prevFilterModel) {
        this.gridService.gridApi?.setFilterModel(prevFilterModel);
      }
    this.gridService.gridApi.setServerSideDatasource({
      getRows(params: IServerSideGetRowsParams) {
        const {startRow, endRow, rowGroupCols, groupKeys, filterModel, sortModel} = params.request;
        const body = {startRow, endRow, rowGroupCols, groupKeys, filterModel, sortModel};
        that.httpService.post(environment.API_PATH + '/product/stage', body).subscribe({
          next: (dataAndCount) => {
            const receivedData = dataAndCount as { rows: {}[], count: number };
            that.gridReady = true;
            params.success({
              rowData: receivedData.rows,
              rowCount: receivedData.count
            });
          },
          error: (err) => {
            console.error('Failed to load stage data:', err);
            params.fail();
          }
        });
      }
    });
  }

  cellEditingStarted($event: any): void {
    console.log('this.auth.currentUserValue.role._id', this.auth.currentUserValue);
    console.log('cellEditingStarted - $event', $event);

    if ($event.data.locked && $event.data.locked?.user && $event.data.locked?.user?._id !== this.auth.currentUserValue._id) {
      this.gridService.gridApi?.stopEditing();
    }

    if ([30].includes($event.data.status)) {
      this.gridService.gridApi?.stopEditing();
    } else {
      this.httpService.post(environment.API_PATH + '/product/stage/edit/start', {
        product: {
          _id: $event.data._id,
          field: $event.colDef.field,
        },
        user: {
          _id: this.auth.currentUserValue._id,
          name: this.auth.currentUserValue.name
        }
      }).subscribe(res => {
        console.log(res);
      });
    }
  }

  columnEverythingChanged($event: any): void {
    if (this.gridReady) {
      const savedState = this.gridService.columnApi?.getColumnState();
      this.localStorageService.setItem('stageColumnState', savedState);
    }
  }

  filterChanged($event: any): void {
    const filterModel = this.gridService.gridApi?.getFilterModel();
    this.localStorageService.setItem('stageFilterModel', filterModel);
    this.gridService.stageFilterIsActive$.next(!!(filterModel && Object.keys(filterModel).length));
    this.filterIsActive = !!(filterModel && Object.keys(filterModel).length);
  }

  cancelLastChangeSelected() {
    const selectedRowsIds: any = this.gridService.gridApi?.getSelectedRows().map((r: any) => r.id);
    this.http.post(environment.API_PATH + '/product/cancel-changes', {
      product_id: selectedRowsIds
    }).subscribe((res: any) => {
      this.pendingCancel = false;
    });
  }

  cancelLastChange(postData?: any): void {
    this.pendingCancel = true;
    if (postData) {
      this.http.post(environment.API_PATH + '/product/cancel-changes', postData).subscribe((res: any) => {
        this.pendingCancel = false;
      });
    } else {
      this.http.get(environment.API_PATH + '/product/cancel-changes').subscribe((res: any) => {
        this.pendingCancel = false;
      });
    }
  }

  public openLogs(): void {
    this.router.navigate(['grid/logs']);
  }

  clearAllFilters(): void {
    this.gridService.gridApi?.setFilterModel(null);
  }

  viewAllChangesProduct(): void {
    this.gridService.gridApi?.setFilterModel(null);
    this.gridService.gridApi?.setFilterModel({
      shopify_sync_status: {
        filterType: 'text',
        type: 'contains',
        filter: 3
      }
    });
  }

  onSelectionChanged($event: any): void {
    const selectedRows: any = this.gridService.gridApi?.getSelectedRows();
    this.selectedItemsCount = selectedRows.length;
    this.gridService.stageSelectedItemsCount$.next(selectedRows.length);
    console.log('🤲 selectedRows', selectedRows);
    this.gridService.gridApi?.getSelectedNodes().forEach((rowNode: RowNode) => {
      // console.log('row', rowNode);
      if (rowNode.data.locked && rowNode.data.locked.user) {
        rowNode.setSelected(false);
      }
    });
  }

  updateData(data: any): void {
    this.httpService.patch(environment.API_PATH + '/product/stage', data).subscribe(resp => {
      // this.toastService.success(resp.message);
    });

  }

  onCellClicked( e: CellClickedEvent): void {
    // e.node.setSelected(false);
    // e.node.se
  }

  getContextMenuItems = (params: GetContextMenuItemsParams) => {
    console.log('params', params);
    let restoreValue: any = null;
    const cellId: any = params.column?.getId();
    // const disableHistoryBtn2 = params.node?.data.changes === undefined || params.node?.data.changes[cellId] === undefined;
    const disableHistoryBtn = params.node?.data.logs[cellId] === false;
      return [
        {
          name: 'Open BHS Shopify Page',
          disabled: environment.staging === true ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://blackhawksupply.com/products/' + params?.node?.data.handle, '_blank');
          }
        },
        {
          name: 'Open BHS Shopify Admin Page',
          disabled: environment.staging === true ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://admin.shopify.com/store/bhawk/products/' + params?.node?.data.shopify_id.replace('gid://shopify/Product/', ''), '_blank');
          }
        },
        'separator',
        {
          name: 'Open MWS Shopify Page',
          disabled: environment.staging === true ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://midwestsupplyus.com/products/' + params?.node?.data.handle, '_blank');
          }
        },
        {
          name: 'Open MWS Shopify Admin Page',
          disabled: environment.staging === true ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://admin.shopify.com/store/midwest-supply-us/products/' + params?.node?.data.mws_shopify_id.replace('gid://shopify/Product/', ''), '_blank');
          }
        },
        'separator',
        {
          name: 'Open Both Shopify Pages',
          disabled: environment.staging === true ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://blackhawksupply.com/products/' + params?.node?.data.handle, '_blank');
            window.open('https://midwestsupplyus.com/products/' + params?.node?.data.handle, '_blank');
          }
        },
        'separator',
        {
          name: 'Open <b>STAGE</b> Shopify Page',
          disabled: environment.staging === false ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://bhawk1.myshopify.com/products/' + params?.node?.data.handle, '_blank');
            window.open('https://midwestsupplyus.com/products/' + params?.node?.data.handle, '_blank');
          }
        },
        {
          name: 'Open <b>STAGE</b> Admin Page',
          disabled: environment.staging === false ? true : false,
          icon: '<span class="ag-icon ag-icon-eye" unselectable="on" role="presentation"></span>',
          action: () => {
            window.open('https://admin.shopify.com/store/bhawk1/products/' + params?.node?.data.shopify_id.replace('gid://shopify/Product/', ''), '_blank');
          }
        },
        'separator',
        {
          name: 'Auto Size Column',
          icon: '<span class="ag-icon ag-icon-maximize" unselectable="on" role="presentation"></span>',
          action: () => {
            const colId: any = params.column?.getId();
            this.gridService.columnApi?.autoSizeColumn(colId);
          }
        },
        {
          name: params.node?.rowPinned ? 'Unpin Row' : 'Pin Row',
          icon: '<span class="ag-icon ag-icon-pin" unselectable="on" role="presentation"></span>',
          disabled: params.node?.rowPinned === undefined && this.pinnedTopRowData.findIndex((x: any) => x.id === params.node?.data?.id) !== -1,
          action: () => {
            // const colId: any = params.column?.getId();
            if (params.node?.rowPinned) {
              let index = this.pinnedTopRowData.findIndex((x: any) => x.id === params.node?.data?.id);
              // console.log('index', index);
              if (index !== -1) {
                this.pinnedTopRowData.splice(index, 1);
                this.gridService.gridApi?.setPinnedTopRowData(this.pinnedTopRowData);
              }
            } else {
              this.pinnedTopRowData.push(params?.node?.data);
              this.gridService.gridApi?.setPinnedTopRowData(this.pinnedTopRowData);
            }
            // console.log('this.pinnedTopRowData', this.pinnedTopRowData);
          }
        },
        {
          name: 'History',
          icon: '<span class="ag-icon ag-icon-group" unselectable="on" role="presentation"></span>',
          disabled: disableHistoryBtn,
          action: () => {
            this.dialog.open(HistoryDialogComponent, {
              width: '90%',
              maxWidth: '1024px',
              disableClose: true,
              data: {
                params,
                sku: params.node?.data.sku,
                column: params.column?.getColDef().headerName,
                product_id: params.node?.data._id,
                field: params.column?.getId()
              }
            });
          }
        },
        restoreValue,
    ];
  }

  cellEditingStopped($event: any): void {
    console.log('cellEditingStopped', $event);
    if ($event.newValue === undefined || ($event.oldValue === $event.newValue)) {
      this.httpService.post(environment.API_PATH + '/product/stage/edit/stop', {}).subscribe((res: any) => {});
    }
  }

  cellEditRequest($event: any): void {
    console.log('dddd');
  }

  cellValueChanged($event: any): void {
    // console.log('cellValueChanged ----- $event', $event);
    if (JSON.stringify($event.value) !== JSON.stringify($event.oldValue)) {
      console.log('✅', {
        product_id: $event.data._id,
        payload: {
          [$event.colDef.field]: $event.newValue,
        }
      });
      this.updateData({
        product_id: $event.data._id,
        payload: {
          [$event.colDef.field]: $event.newValue,
        }
      });
    }
  }
  cellValueChanged2($event: any): void {
    console.log('🤟cellValueChanged', $event);
    if ($event.newValue !== undefined) {
      if ($event.column.colId === 'replacement_sku') {
        this.updateData({
          product_id: $event.data._id,
          payload: {
            replacement_sku_id: $event.data.replacement_sku_id,
            replacement_sku: $event.data.replacement_sku,
            obsolete: $event.data.obsolete
          }
        });
        // const title_suffix_1__mod = $event.data.obsolete ? ' (OBSOLETE)' : '';
        // this.updateData({
        //   id: $event.data.id,
        //   data: {
        //     replacement_sku_id: $event.data.replacement_sku_id,
        //     replacement_sku: $event.data.replacement_sku,
        //     obsolete: $event.data.obsolete,
        //     shopify_sync_status: false,
        //     title_suffix_1: title_suffix_1__mod
        //   }
        // });
      } else if ($event.column.colId === 'brand_name') {
        this.updateData({
          id: $event.data._id,
          data: {
            brand_id: $event.data.brand_id,
            shopify_sync_status: false
          }
        });
      } else if ($event.column.colId === 'tags') {
        // const body = {
        //   id: $event.data.id,
        //   data: $event.value,
        //   type: 'tags'
        // };
        console.log('$event.value', $event.value);
        const body = {
          product_id: $event.data._id,
          payload: $event.value.map((tag: any) => {
            return {
              id: tag.id,
              tag_id: tag.tag_id
            }
          }),
          type: 'tags'
        };
        this.updateData(body);
        // this.http.post(environment.API_PATH + '/product/update', body).subscribe(res => {
        //   this.toastService.success('Data Saved Successfully');
        // });
      } else {
        this.updateData({
          product_id: $event.data._id,
          payload: {
            [$event.colDef.field]: $event.newValue,
          }
        });
      }
    }
  }

  createFeedChanged(): void {
    this.http.get(environment.API_PATH + '/product/all-changes').subscribe((res: any) => {
      this.createFeedSelected(res.map((x: any) => x.id));
    });
  }

  createFeedFiltered(): void {
    let filterModel = this.gridService.gridApi?.getFilterModel();
    const body = {startRow: 0, endRow: 100, rowGroupCols: [], groupKeys: [], filterModel, sortModel: []};
    this.http.post(environment.API_PATH + '/feed/filtered', body).subscribe((res: any) => {
      this.toastService.info(res.message);
    });
  }

  createFeedSelected(ids = null): void {
    let selectedIds = ids;
    if (ids === null) {
      const selectedRows: any = this.gridService.gridApi?.getSelectedRows();
      selectedIds = selectedRows.map((x: any) => x.id);
    }
    this.gridService.gridApi?.getSelectedNodes().forEach(row => {
      const data = row.data;
      data.shopify_sync_status = 2;
      row.setData(data);
    });
    this.gridService.gridApi?.showLoadingOverlay();
    this.http.post(environment.API_PATH + '/feed/selected', {
      ids: selectedIds
    }).subscribe((res: any) => {
      this.gridService.gridApi?.hideOverlay();
    });
  }

  bulkEditSelectedDialog(): void {
    const selectedRows: any = this.gridService.gridApi?.getSelectedRows();
    const selectedIds = selectedRows.map((x: any) => x.id);

    this.dialog.open(BulkEditDialogComponent, {
      width: '100vw',
      maxWidth: '95vw',
      data: {
        type: 'selected',
        rows: selectedIds
      }
    });
  }

  bulkEditFilterDialog(): void {
    const filterModel = this.gridService.gridApi?.getFilterModel();
    const body = {startRow: 0, endRow: 100, rowGroupCols: [], groupKeys: [], filterModel, sortModel: []};

    this.dialog.open(BulkEditDialogComponent, {
      width: '100vw',
      maxWidth: '95vw',
      data: {
        type: 'filtered',
        rows: [],
        body
      }
    });
  }
}
